import { useEffect, useRef } from "react";
import QRCodeStyling from "qr-code-styling";

/**
 * StyledQR — a dotted-style QR code, optionally with a CashApp-style badge in
 * the centre (green rounded square, white "$"). Used for the Lightning /
 * CashApp Pay flows (`logo="cashapp"`) and the PayPal / Venmo flow
 * (`logo="none"`). Renders into a <canvas> sized by RENDER_SIZE and scaled
 * down to the parent via CSS, so it stays crisp on retina screens.
 */

// Centre badge: green rounded square + white "$", inlined as an SVG data URI.
const CASHAPP_BADGE =
  "data:image/svg+xml;base64," +
  btoa(
    '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">' +
      '<rect width="120" height="120" rx="30" fill="#00D632"/>' +
      '<text x="60" y="64" font-family="Arial,Helvetica,sans-serif" font-size="82" ' +
      'font-weight="700" fill="#FFFFFF" text-anchor="middle" dominant-baseline="central">$</text>' +
    "</svg>",
  );

const RENDER_SIZE = 512;

export default function StyledQR({
  value,
  className = "",
  logo = "cashapp",
  accentColor = "#00D632",
}: {
  value: string;
  className?: string;
  logo?: "cashapp" | "none";
  /** Colour of the three corner dots. Defaults to CashApp green. */
  accentColor?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<QRCodeStyling | null>(null);

  // Lazily build the QRCodeStyling instance once.
  if (!qrRef.current) {
    qrRef.current = new QRCodeStyling({
      width: RENDER_SIZE,
      height: RENDER_SIZE,
      type: "canvas",
      data: value,
      margin: 6,
      image: logo === "cashapp" ? CASHAPP_BADGE : undefined,
      qrOptions: { errorCorrectionLevel: "M" },
      dotsOptions: { type: "dots", color: "#0A0D12" },
      cornersSquareOptions: { type: "extra-rounded", color: "#0A0D12" },
      cornersDotOptions: { type: "dot", color: accentColor },
      backgroundOptions: { color: "#FFFFFF" },
      imageOptions: {
        crossOrigin: "anonymous",
        margin: 4,
        imageSize: 0.2,
        hideBackgroundDots: true,
      },
    });
  }

  // Mount the canvas into the DOM.
  useEffect(() => {
    const el = containerRef.current;
    if (el && qrRef.current) {
      el.innerHTML = "";
      qrRef.current.append(el);
    }
  }, []);

  // Re-render when the encoded value changes.
  useEffect(() => {
    qrRef.current?.update({ data: value });
  }, [value]);

  return (
    <div
      ref={containerRef}
      className={`[&>canvas]:!w-full [&>canvas]:!h-full [&>canvas]:block ${className}`}
    />
  );
}
